import { useRequest } from 'ahooks'
import { useId } from 'react'

import { asyncSuccessResolver, toast } from '$app/utilities'
import {
  errorMessageResolver,
  errorStatusResolver,
  successResolver
} from '$services/api/common'

import { FactorHookOptions } from './types'
import useRecoveryCodeVerifyBlock, {
  UseRecoveryCodeVerifyBlockOptions
} from '$actions/useRecoveryCodeVerifyForm'

type UseRecoveryCodeOptions = FactorHookOptions

export const useRecoveryCode = ({
  preferred_mfa_token_type = 'ott',
  apiInstance: { mfaChallengeRecoveryCodeVerify },
  onVerified
}: UseRecoveryCodeOptions) => {
  const toastID = useId()

  const onApiError = (err: unknown) => {
    if (errorStatusResolver(err) === 401) {
      resetActions()
    }
  }

  const verifyReq = useRequest(
    asyncSuccessResolver(mfaChallengeRecoveryCodeVerify, successResolver),
    {
      manual: true,
      onError: onApiError,
      onSuccess: res => onVerified(res.data.mfa_token)
    }
  )

  const requests = [verifyReq]

  const isAnyLoading = requests.map(({ loading }) => loading).includes(true)
  const disabled = isAnyLoading

  const onSuccess: UseRecoveryCodeVerifyBlockOptions['onSuccess'] = (
    resetForm,
    { recoveryCode: code }
  ) => {
    const { runAsync, cancel } = verifyReq

    cancel()

    toast
      .promise(runAsync({ code, type: preferred_mfa_token_type }), {
        error: errorMessageResolver,
        loading: 'Verifying..',
        success: 'Verified.',
        id: toastID
      })
      .unwrap()
      .finally(resetForm)
  }

  const formProps = useRecoveryCodeVerifyBlock({
    title: 'Recovery Code Verification',
    description: 'Please enter your recovery code to verify your identity.',
    submitButtonText: 'Verify',
    onSuccess,
    disabled
  })

  const resetActions = () => {
    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })
  }

  const reset = () => {
    resetActions()
    toast.dismiss(toastID)
  }

  return {
    isAnyLoading,
    reset,
    formProps
  }
}

export default useRecoveryCode
